import { scaleLinear, scalePoint } from 'd3-scale';
import { curveLinear, line } from 'd3-shape';

/**
 * A WORD-SIZED GRAPHIC, AND IT NEVER SHIPS ALONE (`dashboard.md` D29).
 *
 * Tufte's definition is the specification: "a small intense, simple, word-sized graphic
 * with typographic resolution", placed "everywhere a word or number can be", and "Avoid
 * all data frames". So: 64x16, one 2px polyline in `--color-ink-subtle`, a 4px dot on the
 * last point (neutral since the redesign, see the circle below), no axis, no grid, no frame,
 * no tooltip.
 *
 * THE END DOT IS THE HALF THAT MAKES IT A SPARKLINE. D29 quotes Tufte on why: "Lacking a
 * scale of measurement, this free-floating line is dequantified ... Both representations
 * of the most recent reading are tied together with a colour accent." A sparkline without
 * the NUMBER beside it states a shape and refuses to say a magnitude, which is why this
 * component is only reachable through the tile's slot (`ui/Tile`, which merged `StatTile` and
 * `KpiTile`) or a trend cell that prints the latest figure beside it (register L16).
 *
 * BELOW THE MINIMUM IT DRAWS NOTHING, which is also D29's rule, taken from Apple's Fitness
 * Trends ("It takes 180 days of activity to start your trends"): a trend over two points
 * is not a smaller trend, it is a different and wronger claim. Two readings make a line
 * with one segment and a slope of whatever noise there was that week.
 */
export const SPARKLINE_MIN_POINTS = 3;

/**
 * THREE SIZES, ONE DRAWING (fe-foundation, redesign register D10, L16; components page
 * section 3). `word` is the original 64x16; `tile` is the KPI tile's (dashboard-v2-side.html:153)
 * and `cell` the Clients trend cell's (clients-v2.html:168). The box is sized by its tokens
 * (`--sparkline-tile-*`, `--sparkline-cell-*`) and the geometry below draws in the same units,
 * so the line is never scaled and its stroke stays 2px at every size.
 */
export type SparklineSize = 'word' | 'tile' | 'cell';

const GEOMETRY: Readonly<
  Record<SparklineSize, { readonly width: number; readonly height: number; readonly box: string }>
> = {
  word: { width: 64, height: 16, box: '' },
  tile: {
    width: 88,
    height: 30,
    box: 'w-(--sparkline-tile-width) h-(--sparkline-tile-height)',
  },
  cell: {
    width: 56,
    height: 20,
    box: 'w-(--sparkline-cell-width) h-(--sparkline-cell-height)',
  },
};

export interface SparklineProps {
  /** In time order, oldest first. `null` is a gap, never a zero. */
  readonly points: readonly (number | null)[];
  /**
   * The sentence a screen reader gets. Without one the graphic is decorative, which is the
   * right answer wherever the number beside it already says everything.
   */
  readonly label?: string | undefined;
  readonly size?: SparklineSize | undefined;
}

/** Room for the 4px end dot's radius plus the 2px stroke, so nothing is clipped. */
const INSET = 3;

export function Sparkline({ points, label, size = 'word' }: SparklineProps) {
  const { width: WIDTH, height: HEIGHT, box: sizeBox } = GEOMETRY[size];
  const drawable = points.filter((value): value is number => value !== null);
  if (drawable.length < SPARKLINE_MIN_POINTS) return null;

  const x = scalePoint<number>()
    .domain(points.map((_, index) => index))
    .range([INSET, WIDTH - INSET]);

  const min = Math.min(...drawable);
  const max = Math.max(...drawable);
  // A flat series would divide by zero; it draws down the middle, which is what it is.
  const y = scaleLinear()
    .domain(min === max ? [min - 1, max + 1] : [min, max])
    .range([HEIGHT - INSET, INSET]);

  const path = line<{ index: number; value: number | null }>()
    .defined((point) => point.value !== null)
    .curve(curveLinear)
    .x((point) => x(point.index) ?? INSET)
    .y((point) => y(point.value ?? 0))(points.map((value, index) => ({ index, value })));

  // The last reading that EXISTS, which is not always the last slot: a series whose most
  // recent week has not been written yet still has a most recent number.
  let lastIndex = -1;
  for (const [index, value] of points.entries()) {
    if (value !== null) lastIndex = index;
  }
  const lastValue = lastIndex === -1 ? null : (points[lastIndex] ?? null);

  const marks = (
    <>
      {path !== null && (
        <path
          d={path}
          fill="none"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="stroke-ink-subtle"
        />
      )}
      {lastValue !== null && (
        /* NEUTRAL, NOT THE ACCENT (redesign register R-K1, components page section 3
           "Decided"): blue is kept for what can be clicked (L6), and the dot is the most
           recent reading, not a control. A darker step of the same neutral ink than the
           line, so the last reading still stands out. */
        <circle cx={x(lastIndex) ?? INSET} cy={y(lastValue)} r={2} className="fill-ink-muted" />
      )}
    </>
  );

  const box = {
    width: WIDTH,
    height: HEIGHT,
    viewBox: `0 0 ${String(WIDTH)} ${String(HEIGHT)}`,
    className: `block shrink-0 ${sizeBox}`.trim(),
  } as const;

  /**
   * NAMED OR HIDDEN, AND WRITTEN OUT BOTH WAYS. A conditional `role` is a role the linter
   * that guards SVG alternative text cannot see, and that rule is the reason this file
   * cannot quietly ship a graphic nobody can read.
   */
  return label === undefined ? (
    <svg {...box} aria-hidden="true">
      {marks}
    </svg>
  ) : (
    <svg {...box} role="img" aria-label={label}>
      {marks}
    </svg>
  );
}
